// screens/RadarProposal.tsx — R3b (plate 03), on spread.css like the other
// spreads. Approve/decline, the body rendered from body_md, and the two
// receipt lines (approve/decline never vanish a proposal silently —
// receipts, not acknowledgements, the-voice-canon §2).
//
// R3b-2 (2026-09-27): the receipt text is Pythia's own, verbatim
// (`pythia_radar_command`'s immediate-command receipt) — never a
// client-composed sentence (item 4).
import { useEffect, useState } from 'preact/hooks';
import {
  ApiError,
  addToRadarNewsletter,
  approveRadarProposal,
  declineRadarProposal,
  getRadarProposal,
  openRadarItemOriginal,
  raiseRadarItem,
  type RadarItemApi,
  type RadarProposalApi,
  type RadarSourceApi,
} from '../lib/api';
import { BackLink } from '../components/BackLink';
import { MdLite } from '../components/MdLite';
import { kindChipLabel } from '../lib/radarKind';
import './spread.css';
import './RadarProposal.css';

export interface RadarProposalProps {
  tenantId: string;
  id: string;
  onBack: () => void;
}

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; proposal: RadarProposalApi; items: Array<{ item: RadarItemApi | null; source: RadarSourceApi | null }> };

type Decision = { text: string; at: string } | null;

function timeLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

export function RadarProposal({ tenantId, id, onBack }: RadarProposalProps) {
  const [load, setLoad] = useState<LoadState>({ phase: 'loading' });
  const [busy, setBusy] = useState(false);
  const [decision, setDecision] = useState<Decision>(null);
  const [note, setNote] = useState<string | null>(null);
  // R3d (C5) — "Add to build list", on the item card.
  const [raiseBusy, setRaiseBusy] = useState<string | null>(null);
  const [raiseReceipts, setRaiseReceipts] = useState<Record<string, string>>({});
  // R3e (C) — "Add to newsletter", the same shape, beside it.
  const [newsletterBusy, setNewsletterBusy] = useState<string | null>(null);
  const [newsletterReceipts, setNewsletterReceipts] = useState<Record<string, string>>({});

  function refresh() {
    return getRadarProposal(tenantId, id)
      .then((r) => setLoad({ phase: 'ready', proposal: r.proposal, items: r.items }))
      .catch((err: unknown) => {
        setLoad({ phase: 'error', message: err instanceof ApiError ? err.message : 'the radar is not reachable' });
      });
  }

  useEffect(() => {
    setLoad({ phase: 'loading' });
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, id]);

  async function approve() {
    setBusy(true);
    setNote(null);
    try {
      const r = await approveRadarProposal(tenantId, id);
      setDecision({ text: r.receipt ?? `filed as a to-do: "${r.task.title}"`, at: new Date().toISOString() });
    } catch (err) {
      setNote(err instanceof ApiError ? err.message : 'not recorded');
    } finally {
      setBusy(false);
    }
  }

  async function decline() {
    setBusy(true);
    setNote(null);
    try {
      const r = await declineRadarProposal(tenantId, id);
      setDecision({ text: r.receipt ?? 'kept under the topic; nothing filed', at: new Date().toISOString() });
    } catch (err) {
      setNote(err instanceof ApiError ? err.message : 'not recorded');
    } finally {
      setBusy(false);
    }
  }

  async function readOurCopy(fileId: string) {
    try {
      await openRadarItemOriginal(tenantId, fileId);
    } catch (err) {
      setNote(err instanceof ApiError ? err.message : 'could not open the kept copy');
    }
  }

  async function addToBuildList(item: RadarItemApi) {
    setRaiseBusy(item.id);
    try {
      const r = await raiseRadarItem(tenantId, item.id);
      setRaiseReceipts((cur) => ({ ...cur, [item.id]: r.receipt ?? `added to Worth building — "${r.proposal?.title ?? item.title ?? item.url}"` }));
    } catch (err) {
      setRaiseReceipts((cur) => ({ ...cur, [item.id]: err instanceof ApiError ? err.message : 'not recorded' }));
    } finally {
      setRaiseBusy(null);
    }
  }

  // R3e (C) — "Add to newsletter", beside "Add to build list"; the tool's
  // own receipt, verbatim.
  async function addToNewsletter(item: RadarItemApi) {
    setNewsletterBusy(item.id);
    try {
      const r = await addToRadarNewsletter(tenantId, item.id);
      setNewsletterReceipts((cur) => ({ ...cur, [item.id]: r.receipt ?? `added to the newsletter — "${item.title ?? item.url}"` }));
    } catch (err) {
      setNewsletterReceipts((cur) => ({ ...cur, [item.id]: err instanceof ApiError ? err.message : 'not recorded' }));
    } finally {
      setNewsletterBusy(null);
    }
  }

  if (load.phase === 'loading') {
    return (
      <div class="sp">
        <BackLink label="Radar" onClick={onBack} />
        <div class="sp__say">One moment…</div>
      </div>
    );
  }
  if (load.phase === 'error') {
    return (
      <div class="sp">
        <BackLink label="Radar" onClick={onBack} />
        <div class="sp__say">
          The radar is not reachable.
          <br />
          <span class="sp__say-soft">{load.message}</span>
        </div>
      </div>
    );
  }

  const { proposal, items } = load;

  return (
    <div class="sp">
      <BackLink label="Radar" onClick={onBack} />
      <h1 class="sp__title">{proposal.title}</h1>

      {decision ? (
        <div class="rprop__receipt">{timeLabel(decision.at)} — {decision.text}</div>
      ) : (
        <>
          <div class="rprop__acts">
            <button type="button" class="sp__act" disabled={busy} onClick={() => void approve()}>
              Approve — file as a to-do
            </button>
            <button type="button" class="sp__act" disabled={busy} onClick={() => void decline()}>
              Not now
            </button>
          </div>
          <p class="rprop__soft">
            Approving files it on your spine with this chain attached. Nothing is built by the tap.
          </p>
          {note ? <p class="sp__age sp__age--late">{note}</p> : null}
        </>
      )}

      <div class="sp__grid">
        <div class="sp__hero">
          <MdLite md={proposal.bodyMd} />
        </div>

        <div class="sp__aside">
          {items.map(({ item, source }, i) => (
            <div key={item?.id ?? i}>
              <div class="sp__sec">
                <span>
                  {/* R3d (C1) — the kind chip, the same word (never an icon
                      alone) the topic screen's item rows lead with. */}
                  {item ? <span class="sp__chip" style={{ marginRight: '6px' }}>{kindChipLabel(item.kind, item.durationSeconds)}</span> : null}
                  item {i + 1} · {item?.verdict ?? '—'}
                </span>
              </div>
              {/* R3e (C, tunnel walk feedback): same renderer as this
                  proposal's own body, with the reading's duplicate heading
                  (already shown as "item N · verdict" above it) dropped. */}
              {item?.readingMd ? <MdLite md={item.readingMd} dedupeTitle={item.title} /> : <div class="sp__empty">No reading kept.</div>}
              {item ? (
                <div class="rtopic__row-acts">
                  {/* R3d (C5) — "Add to build list", on the item card too. */}
                  {raiseReceipts[item.id] ? (
                    <span class="rtopic__raise-receipt">{raiseReceipts[item.id]}</span>
                  ) : (
                    <button
                      type="button"
                      class="rtopic__link rtopic__link--action"
                      disabled={raiseBusy === item.id}
                      onClick={() => void addToBuildList(item)}
                    >
                      Add to build list
                    </button>
                  )}
                  {/* R3e (C) — "Add to newsletter", the same shape, beside it. */}
                  {newsletterReceipts[item.id] ? (
                    <span class="rtopic__raise-receipt">{newsletterReceipts[item.id]}</span>
                  ) : (
                    <button
                      type="button"
                      class="rtopic__link rtopic__link--action"
                      disabled={newsletterBusy === item.id}
                      onClick={() => void addToNewsletter(item)}
                    >
                      Add to newsletter
                    </button>
                  )}
                </div>
              ) : null}

              <div class="sp__sec" style={{ marginTop: '12px' }}><span>Source</span></div>
              {item ? (
                <div class="rprop__source">
                  <a class="rtopic__item-title" href={item.url} target="_blank" rel="noreferrer">{item.url}</a>
                  <div class="sp__row-sub">
                    Found on {source?.name ?? 'an unnamed source'}
                    {item.publishedAt ? `, ${new Date(item.publishedAt).toLocaleDateString([], { month: 'short', day: 'numeric' })}` : ''}
                    {item.relevance !== null ? ` · relevance ${item.relevance} of 10` : ''}
                  </div>
                  {item.fileId ? (
                    <div class="rprop__file">
                      Kept in Pythia as file {item.fileId.slice(0, 8)}
                      <button type="button" class="rtopic__link" onClick={() => void readOurCopy(item.fileId!)}>
                        read our copy
                      </button>
                      <a class="rtopic__link" href={item.url} target="_blank" rel="noreferrer">open the original</a>
                    </div>
                  ) : null}
                </div>
              ) : (
                <div class="sp__empty">This item is no longer available.</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
